// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import translate from "@insite/client-framework/Translate";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    saveOrder,
};

export interface SaveOrderStyles {
    saveLink?: LinkPresentationProps;
}

type OwnProps =
    | {
          variant: "button";
          extendedStyles?: ButtonPresentationProps;
      }
    | {
          variant: "clickable";
          extendedStyles?: ClickablePresentationProps;
      }
    | {
          variant: "link";
          extendedStyles?: SaveOrderStyles;
      };

export const saveOrderStyles: SaveOrderStyles = {};

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const { isSavingOrder } = state.pages.cart;
    const { isUpdatingCart } = state.context;
    return {
        isDisabled:
            cartState.isLoading ||
            isSavingOrder ||
            isUpdatingCart ||
            cartState.value?.lineCount === 0 ||
            cartState.value?.cartLines?.length === 0,
        canSaveOrder: cartState.value?.canSaveOrder,
        isSignedIn: cartState.value?.isAuthenticated && !cartState.value?.isGuestOrder,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    HasToasterContext &
    ResolveThunks<typeof mapDispatchToProps>;

const CartSaveOrderButton = ({
    isDisabled,
    canSaveOrder,
    saveOrder,
    history,
    toaster,
    savedOrdersPageUrl,
    cartPageUrl,
    signInPageUrl,
    extendedStyles,
    isSignedIn,
    variant,
}: Props) => {
    const styles = mergeToNew(saveOrderStyles, extendedStyles);
    if (!canSaveOrder) {
        return null;
    }
    // const RenderComponent = variant === "button" ? Button : Clickable;
    const handleClick = () => {
        if (!isSignedIn) {
            history.push(`${signInPageUrl}?returnUrl=${cartPageUrl}`);
            return;
        }

        saveOrder({
            onSuccess: (orderId: string) => {
                history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
        });
    };

    return (
        <Link disabled={isDisabled} data-test-selector="cart_saveOrder" {...styles.saveLink} onClick={handleClick}>
            {translate("Save Order")}
        </Link>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(CartSaveOrderButton)));
